import { ToggleGroup } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface SegmentedOption<V extends string> {
  value: V;
  label: ReactNode;
}

/**
 * Przełącznik segmentowy („Moje / Zespół”, „Wpisy / Raport”, „Czeka · 3 / Historia”).
 * Rozmiar S: 12 px (menu boczne), M: 14 px (nagłówki stron). Zawsze jedna opcja wybrana.
 */
export function Segmented<V extends string>({
  value,
  onValueChange,
  options,
  size = 'm',
  fullWidth = false,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: {
  value: V;
  onValueChange: (value: V) => void;
  options: SegmentedOption<V>[];
  size?: 's' | 'm';
  fullWidth?: boolean;
  /** cała grupa nieaktywna (np. kategoria push przy wyłączonych powiadomieniach) */
  disabled?: boolean;
  className?: string;
  'aria-label': string;
}) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={(next) => {
        if (next) onValueChange(next as V);
      }}
      aria-label={ariaLabel}
      disabled={disabled}
      className={cn(
        'inline-flex gap-0.5 rounded-lg bg-elevated p-0.5',
        fullWidth && 'flex w-full',
        disabled && 'opacity-50',
        className,
      )}
    >
      {options.map((option) => (
        <ToggleGroup.Item
          key={option.value}
          value={option.value}
          className={cn(
            'inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap text-fg-secondary transition-colors',
            'hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
            'data-[state=on]:bg-canvas data-[state=on]:text-fg data-[state=on]:shadow-xs',
            size === 's' ? 'h-6 px-3 text-caption' : 'h-8 px-3 text-body-s',
            fullWidth && 'flex-1',
          )}
        >
          {option.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}
